import React, { useState, useEffect } from 'react';
import { DatePicker, Button, Card, Space, Typography, message, Radio, Table, Row, Col, Statistic } from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';
import { BarChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from 'recharts';
import { ArrowUpOutlined, ArrowDownOutlined, MinusOutlined } from '@ant-design/icons';

const { Text } = Typography;
const mesesNomes = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const RelatorioFaturamentoContent = () => {
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [tipoFaturamento, setTipoFaturamento] = useState('mensal');
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(false);

  const [resumoAnterior, setResumoAnterior] = useState(null); // 🌟 Para armazenar mês anterior

  const fetchResumoAnterior = async () => {
    const dataAnterior =
      tipoFaturamento === 'mensal'
        ? selectedDate.subtract(1, 'month').format('YYYY-MM-DD')
        : selectedDate.subtract(1, 'year').format('YYYY-MM-DD');

    const params = { tipo: tipoFaturamento, data: dataAnterior };

    try {
      const [fatRes, lucroRes] = await Promise.all([
        axios.get('/api/relatorios/faturamento', { params }),
        axios.get('/api/relatorios/lucro-diario', { params })
      ]);

      const faturamento = Array.isArray(fatRes.data) ? fatRes.data : [];
      const lucro = Array.isArray(lucroRes.data) ? lucroRes.data : [];

      const totalFat = faturamento.reduce((acc, f) => acc + Number(f.total ?? f.receita ?? 0), 0);
      const totalLucro = lucro.reduce((acc, l) => acc + Number(l.lucro ?? 0), 0);
      const mediaLucro = lucro.length > 0 ? totalLucro / lucro.length : 0;

      setResumoAnterior({
        totalFat,
        totalLucro,
        mediaLucro
      });

    } catch (err) {
      setResumoAnterior({ totalFat: 0, totalLucro: 0, mediaLucro: 0 });
    }
  };

  const fetchDados = async () => {
    if (!selectedDate) return;
    setLoading(true);
    try {
      const params = { tipo: tipoFaturamento, data: selectedDate.format('YYYY-MM-DD') };
      const [faturamentoRes, lucroRes] = await Promise.all([
        axios.get('/api/relatorios/faturamento', { params }),
        axios.get('/api/relatorios/lucro-diario', { params })
      ]);

      const faturamento = Array.isArray(faturamentoRes.data) ? faturamentoRes.data : [];
      const lucro = Array.isArray(lucroRes.data) ? lucroRes.data : [];

      const dadosUnidos = faturamento.map(f => {
        const l = lucro.find(l => l.periodo === f.periodo) || {};

        return {
          periodo: tipoFaturamento === 'mensal'
            ? `${parseInt(f.periodo, 10)}`
            : mesesNomes[parseInt(f.periodo, 10) - 1],
          Faturamento: Number(f.total ?? f.receita ?? 0),
          lucro: Number(l.lucro ?? 0)
        };
      });

      const mediaDiaria = dadosUnidos.length > 0
        ? dadosUnidos.reduce((acc, d) => acc + d.lucro, 0) / dadosUnidos.length
        : 0;

      setDados(dadosUnidos.map(d => ({ ...d, media: mediaDiaria })));
      fetchResumoAnterior(); // 🌟 PUXA RESUMO DO MÊS ANTERIOR

    } catch (err) {
      message.error('Erro ao buscar dados.');
      setDados([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDados();
  }, [selectedDate, tipoFaturamento]);

  // 🔥 RESUMOS DO MÊS ATUAL
  const totalFaturamento = dados.reduce((acc, d) => acc + d.Faturamento, 0);
  const totalLucro = dados.reduce((acc, d) => acc + d.lucro, 0);
  const mediaDiariaLucro = dados.length > 0 ? totalLucro / dados.length : 0;

  // 🌟 CÁLCULOS DE COMPARAÇÃO COM MÊS ANTERIOR
  const diff = (atual, anterior) => {
    if (!resumoAnterior) return null;
    return atual - anterior;
  };

  const renderDiff = valor => {
    if (valor === null) return null;

    if (valor > 0)
      return <span style={{ color: 'green', marginLeft: 8 }}>▲ {valor.toFixed(2)}</span>;

    if (valor < 0)
      return <span style={{ color: 'red', marginLeft: 8 }}>▼ {valor.toFixed(2)}</span>;

    return <span style={{ color: 'gray', marginLeft: 8 }}>● 0,00</span>;
  };

  return (
    <div>

      {/* 🔥🔥 CARDS DE RESUMO COM COMPARAÇÃO 🔥🔥 */}
      <Row gutter={16} style={{ marginBottom: 20 }}>
        <Col span={8}>
          <Card>
            <Statistic
              title="Faturamento Total"
              prefix="R$"
              value={totalFaturamento}
              precision={2}
            />
            {renderDiff(diff(totalFaturamento, resumoAnterior?.totalFat))}
          </Card>
        </Col>

        <Col span={8}>
          <Card>
            <Statistic
              title="Lucro Total (s/ despesa)"
              prefix="R$"
              value={totalLucro}
              precision={2}
            />
            {renderDiff(diff(totalLucro, resumoAnterior?.totalLucro))}
          </Card>
        </Col>

        <Col span={8}>
          <Card>
            <Statistic
              title="Média Lucro Diário Total"
              prefix="R$"
              value={mediaDiariaLucro}
              precision={2}
            />
            {renderDiff(diff(mediaDiariaLucro, resumoAnterior?.mediaLucro))}
          </Card>
        </Col>
      </Row>

      {/* CONTROLES */}
      <Space wrap style={{ marginBottom: 16 }}>
        <DatePicker
          value={selectedDate}
          onChange={setSelectedDate}
          picker={tipoFaturamento === 'anual' ? 'year' : 'month'}
          style={{ minWidth: 180 }}
        />
        <Button type="primary" onClick={fetchDados}>Buscar</Button>
        <Radio.Group value={tipoFaturamento} onChange={e => setTipoFaturamento(e.target.value)}>
          <Radio.Button value="mensal">Mensal</Radio.Button>
          <Radio.Button value="anual">Anual</Radio.Button>
        </Radio.Group>
      </Space>

      {/* GRÁFICO */}
      <Card title="Gráfico" style={{ marginBottom: 20 }}>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={dados}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="periodo" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="Faturamento" fill="#1890ff" />
            <Line type="monotone" dataKey="lucro" stroke="#52c41a" strokeWidth={2} />
            <Line type="monotone" dataKey="media" stroke="#FFA500" strokeWidth={2} dot={false} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* TABELA */}
<Card title="Tabela">
  <Table
    dataSource={dados}
    columns={[
      {
        title: tipoFaturamento === 'anual' ? 'Mês' : 'Dia',
        dataIndex: 'periodo'
      },
      {
        title: 'Faturamento (R$)',
        dataIndex: 'Faturamento',
        render: (value) => Number(value).toFixed(2)
      },
      {
        title: 'Lucro (R$)',
        dataIndex: 'lucro',
        render: (value) => Number(value).toFixed(2)
      },
      {
        title: 'Média (R$)',
        dataIndex: 'media',
        render: (value) => Number(value).toFixed(2)
      }
    ]}
    rowKey="periodo"
    loading={loading}
    pagination={false}
    size="small"
  />
</Card>

    </div>
  );
};

export default RelatorioFaturamentoContent;
